import React, { FunctionComponent } from "react";
import Link from "next/link";

import { removeUrlPrefix, sanitizeUrl } from "src/lib/functions";
import { toJpeg } from "src/lib/imageCompressor";
import { Org, User } from "src/lib/graphql/types";
import { isUser } from "src/lib/globalTypes";
import { useUser } from "src/hooks/useUser";

import PencilIcon from "../../icons/PencilIcon";
import ProfileHeaderMembersList from "./ProfileHeaderMembersList";

type ProfileHeaderProps = {
  entity: Org | User;
};

const ProfileHeader: FunctionComponent<ProfileHeaderProps> = ({ entity }) => {
  const { location, url } = entity;

  const [user] = useUser();

  const isUserMember = user?.memberOf?.find(
    (info) => info.org?.shortname === entity.shortname
  );

  const name = isUser(entity) ? entity.fullName : entity.name;
  const avatar = isUser(entity) ? entity.image : entity.avatar;

  const parentOrganization = !isUser(entity) && entity.parentOrganization;

  const userCanEdit = isUser(entity)
    ? entity.id === user?.id
    : entity.canUserEdit;

  const editUrl = isUser(entity)
    ? "/account/profile"
    : `/account/orgs/edit/${entity.shortname}`;

  return (
    <div className={"profile-header"}>
      <div className="profile-header-content container">
        <div className="profile-avatar-info">
          <div className={"profile-avatar" + (isUser(entity) ? " user" : "")}>
            <img src={toJpeg(avatar)} />
          </div>
          <div className="profile-info">
            <div className="header-and-edit-button">
              <div className="header">
                <h1 className="profile-name heading-3">{name}</h1>
                <div className="location-website-parent">
                  {location && (
                    <span className="profile-location ellipsis">
                      {location}
                    </span>
                  )}
                  {url && (
                    <span className="profile-website">
                      <a
                        href={sanitizeUrl(url as string)}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="ellipsis"
                      >
                        {removeUrlPrefix(sanitizeUrl(url as string))}
                      </a>
                    </span>
                  )}
                  {parentOrganization && (
                    <span className="part-of">
                      Part of&nbsp;
                      <Link href={`/@${parentOrganization.shortname}`}>
                        <a className="ellipsis">{parentOrganization.name}</a>
                      </Link>
                    </span>
                  )}
                </div>
              </div>
              {userCanEdit && (
                <Link href={editUrl}>
                  <a>
                    <button className="small-text bold blue-button">
                      Edit Profile
                      <PencilIcon />
                    </button>
                  </a>
                </Link>
              )}
            </div>
          </div>
        </div>

        {!isUserMember && <ProfileHeaderMembersList entity={entity} />}
      </div>
    </div>
  );
};

export default ProfileHeader;
